import { useEffect, useState } from "react";
import { api, useAuth } from "../../App";
import { mono, sans, Chip, Btn, Select, Input, Textarea, Field, PageTitle, Drawer } from "./ptUi";

// Daily Tasks — the production unit's per-day to-do + delivery tracker.
// Add / edit / inline-status. Writes to pt_tasks in DM's Mongo. Members see and
// edit only their own rows; leads see and manage everyone's.

const DELIVERED = ["Done", "Partial", "Not Done"];
const WORKFLOW = ["On Track", "At Risk", "Delayed", "Done", "Blocked"];
const today = () => new Date().toISOString().slice(0, 10);

export default function PtrackerTasks() {
  const { user } = useAuth();
  const isLead = ["admin", "srmanager", "manager", "creativedirector", "associatecreativedirector"].includes((user?.role || "").toLowerCase());
  const [date, setDate] = useState(today());
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [lists, setLists] = useState({ departments: [], brands: [], item_types: [], studios: [] });
  const [drawer, setDrawer] = useState(null); // task or {} for new
  const [err, setErr] = useState("");

  const load = () => {
    const qs = date === "all" ? "?date=all" : `?date=${date}`;
    api.get(`/ptracker/tasks${qs}`).then((r) => setTasks(r.data.tasks || [])).catch((e) => setErr(e.response?.data?.detail || "Couldn't load tasks."));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);
  useEffect(() => {
    api.get("/ptracker/refs/members").then((r) => setMembers(r.data.members || [])).catch(() => {});
    api.get("/ptracker/refs/lists").then((r) => setLists(r.data)).catch(() => {});
  }, []);

  const patch = (t, body) => api.put(`/ptracker/tasks/${t.id}`, body).then(load).catch((e) => alert(e.response?.data?.detail || "Update failed"));
  const onWorkflow = (t, v) => {
    if (v === "Blocked" && !t.blocker_reason) {
      const reason = window.prompt("Blocker reason (required):", "");
      if (!reason || !reason.trim()) return;
      patch(t, { workflow_status: v, blocker_reason: reason.trim() });
    } else patch(t, { workflow_status: v });
  };

  const th = { textAlign: "left", fontFamily: mono, fontSize: 10, fontWeight: 700, color: "var(--t3)", letterSpacing: ".08em", textTransform: "uppercase", padding: "8px 10px" };
  const td = { padding: "8px 10px", fontSize: 13, color: "var(--t1)", verticalAlign: "middle" };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1200, margin: "0 auto" }} data-testid="ptracker-tasks">
      <PageTitle title="Daily Tasks" sub="Each department's daily work + delivery status. Stored in Dream Machine."
        right={<Btn onClick={() => setDrawer({ date: date === "all" ? today() : date, department: lists.departments[0] || "", delivered_status: "Not Done", workflow_status: "On Track", owner_id: isLead ? "" : (user?.user_id || user?.id) })}>+ Add task</Btn>} />

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <input type="date" value={date === "all" ? "" : date} onChange={(e) => setDate(e.target.value || "all")}
          style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", color: "var(--t2)", fontFamily: mono, fontSize: 12, padding: "6px 8px" }} />
        <Btn kind={date === "all" ? "primary" : "ghost"} onClick={() => setDate(date === "all" ? today() : "all")}>All dates</Btn>
      </div>

      {err && <div style={{ color: "var(--r)", fontFamily: mono, fontSize: 12, marginBottom: 10 }}>{err}</div>}

      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "var(--s2)" }}>
            <tr>
              {date === "all" && <th style={th}>Date</th>}
              <th style={th}>Owner</th><th style={th}>Dept</th><th style={th}>Item</th><th style={th}>Brand</th>
              <th style={th}>Planned output</th><th style={th}>Delivered</th><th style={th}>Workflow</th><th style={th}>Blocker</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id} style={{ borderTop: "1px solid var(--bdr)", cursor: "pointer" }}
                onClick={() => setDrawer(t)}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--rd)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                {date === "all" && <td style={{ ...td, fontFamily: mono, fontSize: 11, color: "var(--t3)" }}>{t.date}</td>}
                <td style={{ ...td, fontWeight: 600 }}>{t.owner_name || "—"}</td>
                <td style={{ ...td, fontFamily: mono, fontSize: 11, color: "var(--t2)" }}>{t.department}</td>
                <td style={{ ...td, color: "var(--t2)" }}>{t.item_type || "—"}</td>
                <td style={{ ...td, color: "var(--t2)" }}>{t.brand_project || "—"}</td>
                <td style={{ ...td, color: "var(--t2)", maxWidth: 240 }}>{t.planned_output || "—"}</td>
                <td style={td} onClick={(e) => e.stopPropagation()}><Chip value={t.delivered_status} options={DELIVERED} onChange={(v) => patch(t, { delivered_status: v })} /></td>
                <td style={td} onClick={(e) => e.stopPropagation()}><Chip value={t.workflow_status} options={WORKFLOW} onChange={(v) => onWorkflow(t, v)} /></td>
                <td style={{ ...td, fontFamily: mono, fontSize: 11, color: "var(--r)" }}>{t.blocker_reason || "—"}</td>
              </tr>
            ))}
            {tasks.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 40, textAlign: "center", fontFamily: mono, fontSize: 13, color: "var(--t3)" }}>No tasks for this view.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {drawer && (
        <TaskForm task={drawer} members={members} lists={lists} isLead={isLead} me={user}
          onClose={() => setDrawer(null)} onSaved={() => { setDrawer(null); load(); }} />
      )}
    </div>
  );
}

function TaskForm({ task, members, lists, isLead, me, onClose, onSaved }) {
  const [f, setF] = useState({
    date: task.date || new Date().toISOString().slice(0, 10),
    department: task.department || "", item_type: task.item_type || "", brand_project: task.brand_project || "",
    owner_id: task.owner_id || (isLead ? "" : (me?.user_id || me?.id)), planned_output: task.planned_output || "",
    studio_location: task.studio_location || "",
    delivered_status: task.delivered_status || "Not Done", workflow_status: task.workflow_status || "On Track",
    blocker_reason: task.blocker_reason || "", notes: task.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const editing = !!task.id;

  const save = () => {
    if (!f.date || !f.department || !f.owner_id) return alert("Date, department and owner are required.");
    if (f.workflow_status === "Blocked" && !f.blocker_reason.trim()) return alert("Blocked tasks need a blocker reason.");
    setSaving(true);
    const req = editing ? api.put(`/ptracker/tasks/${task.id}`, f) : api.post("/ptracker/tasks", f);
    req.then(onSaved).catch((e) => { alert(e.response?.data?.detail || "Save failed"); setSaving(false); });
  };
  const del = () => { if (window.confirm("Delete this task?")) api.delete(`/ptracker/tasks/${task.id}`).then(onSaved).catch((e) => alert(e.response?.data?.detail || "Delete failed")); };

  return (
    <Drawer title={editing ? "Edit task" : "New task"} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Date"><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} /></Field>
          <Field label="Department"><Select options={lists.departments} value={f.department} onChange={(e) => set("department", e.target.value)} allowEmpty /></Field>
          <Field label="Item type"><Select options={lists.item_types} value={f.item_type} onChange={(e) => set("item_type", e.target.value)} allowEmpty /></Field>
          <Field label="Brand / Project"><Select options={lists.brands} value={f.brand_project} onChange={(e) => set("brand_project", e.target.value)} allowEmpty /></Field>
          <Field label="Owner"><Select options={members.map((m) => ({ value: m.id, label: m.name }))} value={f.owner_id} onChange={(e) => set("owner_id", e.target.value)} allowEmpty style={{ opacity: isLead ? 1 : .6 }} /></Field>
          <Field label="Studio / Location"><Select options={lists.studios} value={f.studio_location} onChange={(e) => set("studio_location", e.target.value)} allowEmpty emptyLabel="None" /></Field>
        </div>
        <Field label="Planned output"><Textarea rows={2} value={f.planned_output} onChange={(e) => set("planned_output", e.target.value)} placeholder="What should exist by end of day?" /></Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Delivered"><Select options={["Done", "Partial", "Not Done"]} value={f.delivered_status} onChange={(e) => set("delivered_status", e.target.value)} /></Field>
          <Field label="Workflow"><Select options={["On Track", "At Risk", "Delayed", "Done", "Blocked"]} value={f.workflow_status} onChange={(e) => set("workflow_status", e.target.value)} /></Field>
        </div>
        {f.workflow_status === "Blocked" && <Field label="Blocker reason (required)"><Input value={f.blocker_reason} onChange={(e) => set("blocker_reason", e.target.value)} /></Field>}
        <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
          <div>{editing && isLead && <Btn kind="danger" onClick={del}>Delete</Btn>}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Btn>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
